import { Autocomplete } from "@/components/ui/autocomplete";
import { CategoryFormSelect } from "@/components/ui/category-form-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { api, type Insumo, type Produto } from "@/lib/api";
import { BTN_OUTLINE_ACTION, BTN_PAGE_PRIMARY_ACTION } from "@/lib/buttonStyles";
import { formatBRL, formatBRLMetrica, parseNumeroBR } from "@/lib/formatBrl";
import { numberToMoneyMask } from "@/lib/moneyMask";
import { formatQuantidadeInput, parseQuantidadeInput } from "@/lib/quantidadeInput";
import { formatDisplayText } from "@/lib/formatDisplayText";
import { StandardModal } from "@/components/StandardModal";
import { ModalPrimaryAction } from "@/components/ModalPrimaryAction";
import { PageContentLoader } from "@/components/PageContentLoader";
import { useClientConfig } from "@/contexts/ClientConfigContext";
import { getClientPrimaryButtonStyle } from "@/lib/clientPrimaryButton";
import { MODAL_BODY_CLASS, PAGE_TITLE_CLASS, TABLE_HEAD_CLASS, TABLE_TH_CLASS } from "@/lib/modalStyles";
import {
  calcularCmvAtual,
  calcularCustoTotalComposicao,
  calcularLucroPorItem,
  calcularMargemLucroPercent,
  calcularPrecoIdeal,
  corCmvAtual,
  custoLinhaComposicao,
} from "@/lib/produtoMetricas";
import {
  FRACAO_MULT,
  inferirFracaoUn,
  opcoesUnidadePorBase,
  unidadeCanonicaParaBase,
} from "@/lib/unidades";
import { UNIDADES_VENDA, normalizarUnidadeVenda, type UnidadeVenda } from "@/lib/unidadeVenda";
import {
  ArrowLeft,
  Check,
  ImageIcon,
  Lightbulb,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Wand2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/lib/appToast";
import { buildFormDraftKey, clearFormDraft, loadFormDraft, saveFormDraft } from "@/lib/formDraft";

const MAX_IMAGEM_BYTES = 5 * 1024 * 1024;
const MAX_DESCRICAO = 150;

function resolveImagemPreview(url: string | null): string | null {
  if (!url) return null;
  if (url.startsWith("blob:") || url.startsWith("http://") || url.startsWith("https://")) return url;
  return url.startsWith("/") ? url : `/${url}`;
}

function SectionBadge({ n }: { n: number }) {
  return (
    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-client-primary text-sm font-semibold text-white">
      {n}
    </span>
  );
}

function FormToggle({
  checked,
  onCheckedChange,
  label,
  helper,
  id,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
  helper: string;
  id: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        <button
          type="button"
          id={id}
          role="switch"
          aria-checked={checked}
          aria-label={label}
          onClick={() => onCheckedChange(!checked)}
          className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
            checked ? "bg-client-primary" : "bg-gray-300"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
              checked ? "translate-x-4" : "translate-x-0"
            }`}
          />
        </button>
        <Label htmlFor={id} className="cursor-pointer font-medium text-foreground">
          {label}
        </Label>
      </div>
      <p className="pl-12 text-xs text-muted-foreground">{helper}</p>
    </div>
  );
}

export const OPCAO_CATEGORIA_OUTRA = "__outra__";

export type ComposicaoItemState = {
  insumo_id: number;
  insumo_nome: string;
  unidade: string;
  unidade_base: string;
  quantidade: string;
};

type Props = {
  produtoId: number | null;
  isAdmin: boolean;
  onCancel: () => void;
  onSaved: () => void;
};

type ProdutoFormDraft = {
  nome: string;
  ncm: string;
  precoVenda: string;
  categoriaSelect: string;
  categoriaOutra: string;
  quantidadeMinima: string;
  descricao: string;
  exibirNoCardapio: boolean;
  controlaEstoque: boolean;
  unidadeVenda: UnidadeVenda;
  estoqueInicial: string;
  imagemUrl: string | null;
  imagemMarkedForRemoval: boolean;
  custoManual: string;
  composicaoItems: ComposicaoItemState[];
};

function buildProdutoFormDraft(params: {
  nome: string;
  ncm: string;
  precoVenda: string;
  categoriaSelect: string;
  categoriaOutra: string;
  quantidadeMinima: string;
  descricao: string;
  exibirNoCardapio: boolean;
  controlaEstoque: boolean;
  unidadeVenda: UnidadeVenda;
  estoqueInicial: string;
  imagemUrl: string | null;
  imagemMarkedForRemoval: boolean;
  custoManual: string;
  composicaoItems: ComposicaoItemState[];
}): ProdutoFormDraft {
  return {
    nome: params.nome,
    ncm: params.ncm,
    precoVenda: params.precoVenda,
    categoriaSelect: params.categoriaSelect,
    categoriaOutra: params.categoriaOutra,
    quantidadeMinima: params.quantidadeMinima,
    descricao: params.descricao,
    exibirNoCardapio: params.exibirNoCardapio,
    controlaEstoque: params.controlaEstoque,
    unidadeVenda: params.unidadeVenda,
    estoqueInicial: params.estoqueInicial,
    imagemUrl: params.imagemUrl,
    imagemMarkedForRemoval: params.imagemMarkedForRemoval,
    custoManual: params.custoManual,
    composicaoItems: params.composicaoItems.map((item) => ({ ...item })),
  };
}

function produtoFormEmptyDraft(): ProdutoFormDraft {
  return buildProdutoFormDraft({
    nome: "",
    ncm: "",
    precoVenda: "",
    categoriaSelect: "",
    categoriaOutra: "",
    quantidadeMinima: "",
    descricao: "",
    exibirNoCardapio: true,
    controlaEstoque: true,
    unidadeVenda: "unidade",
    estoqueInicial: "",
    imagemUrl: null,
    imagemMarkedForRemoval: false,
    custoManual: "",
    composicaoItems: [],
  });
}

function produtoFormDraftSignature(draft: ProdutoFormDraft): string {
  return JSON.stringify({
    ...draft,
    composicaoItems: [...draft.composicaoItems].sort((a, b) => a.insumo_id - b.insumo_id),
  });
}

export default function ProdutoFormPage({ produtoId, isAdmin, onCancel, onSaved }: Props) {
  const isEdit = produtoId != null && produtoId > 0;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nome, setNome] = useState("");
  const [ncm, setNcm] = useState("");
  const [precoVenda, setPrecoVenda] = useState("");
  const [categoriaSelect, setCategoriaSelect] = useState("");
  const [categoriaOutra, setCategoriaOutra] = useState("");
  const [quantidadeMinima, setQuantidadeMinima] = useState("");
  const [descricao, setDescricao] = useState("");
  const [exibirNoCardapio, setExibirNoCardapio] = useState(true);
  const [controlaEstoque, setControlaEstoque] = useState(true);
  const [unidadeVenda, setUnidadeVenda] = useState<UnidadeVenda>("unidade");
  const [estoqueInicial, setEstoqueInicial] = useState("");
  const [imagemUrl, setImagemUrl] = useState<string | null>(null);
  const [imagemFile, setImagemFile] = useState<File | null>(null);
  const [imagemPreview, setImagemPreview] = useState<string | null>(null);
  const [imagemMarkedForRemoval, setImagemMarkedForRemoval] = useState(false);
  const [uploadingImagem, setUploadingImagem] = useState(false);
  const imagemInputRef = useRef<HTMLInputElement>(null);
  const imagemPreviewBlobRef = useRef<string | null>(null);
  const [custoManual, setCustoManual] = useState("");
  const [composicaoItems, setComposicaoItems] = useState<ComposicaoItemState[]>([]);
  const [categoriasList, setCategoriasList] = useState<{ id: number; nome: string }[]>([]);
  const [insumos, setInsumos] = useState<Insumo[]>([]);
  const [cmvIdeal, setCmvIdeal] = useState<number>(0);
  const [originalDraftSignature, setOriginalDraftSignature] = useState<string | null>(null);
  const draftHydratedRef = useRef(false);
  const draftConcluidoRef = useRef(false);

  const [modalComposicaoOpen, setModalComposicaoOpen] = useState(false);
  const [modalDraft, setModalDraft] = useState<ComposicaoItemState[]>([]);
  const [composicaoModalBaseline, setComposicaoModalBaseline] = useState<string>("[]");
  const [insumoBusca, setInsumoBusca] = useState("");

  const { config: clientConfig } = useClientConfig();
  const primaryButtonStyle = getClientPrimaryButtonStyle(clientConfig.primary_color);
  const nfceDisponivel = clientConfig.nfce_disponivel === true;
  const draftKey = useMemo(() => buildFormDraftKey("produto", isEdit ? produtoId : "new"), [isEdit, produtoId]);

  const temComposicao = composicaoItems.length > 0;

  const insumosPorId = useMemo(() => new Map(insumos.map((i) => [i.id, i])), [insumos]);

  const insumoAutocompleteOptions = useMemo(
    () =>
      insumos
        .filter((i) => !modalDraft.some((c) => c.insumo_id === i.id))
        .map((i) => ({
          value: i.id,
          label: i.nome,
          meta: i.unidade ? `Unidade: ${i.unidade}` : undefined,
        })),
    [insumos, modalDraft],
  );

  const carregarInsumos = useCallback(async () => {
    try {
      const lista = await api.listarInsumos();
      setInsumos(lista);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const carregarCmvIdeal = useCallback(async () => {
    try {
      const v = await api.obterCmvIdeal();
      setCmvIdeal(v ?? 0);
    } catch {
      setCmvIdeal(0);
    }
  }, []);

  useEffect(() => {
    void carregarInsumos();
    void carregarCmvIdeal();
    const t = window.setInterval(() => {
      void carregarInsumos();
    }, 15000);
    return () => window.clearInterval(t);
  }, [carregarInsumos, carregarCmvIdeal]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    draftHydratedRef.current = false;
    draftConcluidoRef.current = false;
    (async () => {
      try {
        const cats = await api.listarCategorias();
        if (cancelled) return;
        setCategoriasList(cats);
        if (!isEdit) {
          const base = produtoFormEmptyDraft();
          setOriginalDraftSignature(produtoFormDraftSignature(base));
          const draft = loadFormDraft<ProdutoFormDraft>(draftKey) ?? base;
          setNome(draft.nome);
          setNcm(draft.ncm ?? "");
          setPrecoVenda(draft.precoVenda);
          setCategoriaSelect(draft.categoriaSelect);
          setCategoriaOutra(draft.categoriaOutra);
          setQuantidadeMinima(draft.quantidadeMinima);
          setDescricao(draft.descricao);
          setExibirNoCardapio(draft.exibirNoCardapio);
          setControlaEstoque(draft.controlaEstoque);
          setUnidadeVenda(draft.unidadeVenda);
          setEstoqueInicial(draft.estoqueInicial);
          setImagemUrl(draft.imagemUrl);
          setImagemPreview(resolveImagemPreview(draft.imagemUrl));
          setImagemFile(null);
          setImagemMarkedForRemoval(draft.imagemMarkedForRemoval);
          setCustoManual(draft.custoManual);
          setComposicaoItems(draft.composicaoItems);
          setModalComposicaoOpen(false);
          setModalDraft([]);
          setComposicaoModalBaseline("[]");
          setInsumoBusca("");
          limparInputImagem();
          draftHydratedRef.current = true;
          return;
        }
        setLoading(true);
        const p = await api.obterProduto(produtoId!);
        if (cancelled) return;
        const baseDraft: ProdutoFormDraft = {
          nome: p.nome,
          ncm: p.ncm?.trim() ?? "",
          precoVenda: p.preco_venda != null ? numberToMoneyMask(p.preco_venda) : "",
          categoriaSelect: "",
          categoriaOutra: "",
          quantidadeMinima: p.quantidade_minima != null ? formatQuantidadeInput(p.quantidade_minima) : "0",
          descricao: p.descricao?.trim() ?? "",
          exibirNoCardapio: p.exibir_no_cardapio ?? true,
          controlaEstoque: p.controla_estoque ?? true,
          unidadeVenda: normalizarUnidadeVenda(p.unidade_venda),
          estoqueInicial: "",
          imagemUrl: p.imagem_url ?? null,
          imagemMarkedForRemoval: false,
          custoManual: p.valorAtual != null ? numberToMoneyMask(p.valorAtual) : "",
          composicaoItems: [],
        };
        const cat = (p.categoria || "").trim();
        if (cat && cats.some((c) => c.nome === cat)) {
          baseDraft.categoriaSelect = cat;
          baseDraft.categoriaOutra = "";
        } else if (cat) {
          baseDraft.categoriaSelect = OPCAO_CATEGORIA_OUTRA;
          baseDraft.categoriaOutra = cat;
        }
        if (p.composicao && p.composicao.length > 0) {
          baseDraft.composicaoItems =
            p.composicao.map((c) => {
              const cc = c as { insumo_nome?: string; unidade?: string; unidade_base?: string };
              const baseRaw = (cc.unidade_base ?? cc.unidade ?? "un").trim() || "un";
              const base = baseRaw.toLowerCase();
              const isUn = base === "un" || base === "unidade";
              let qtyDisplay = Number(c.quantidade ?? 0);
              let unidadeDisplay: string;
              if (isUn && typeof c.quantidade === "number") {
                const { fracao, qtd } = inferirFracaoUn(c.quantidade);
                qtyDisplay = qtd;
                unidadeDisplay = fracao;
              } else {
                unidadeDisplay = unidadeCanonicaParaBase((cc.unidade ?? baseRaw).trim() || baseRaw);
              }
              return {
                insumo_id: c.insumo_id,
                insumo_nome: cc.insumo_nome ?? "",
                unidade: unidadeDisplay,
                unidade_base: baseRaw,
                quantidade: String(qtyDisplay),
              };
            });
        }
        setOriginalDraftSignature(produtoFormDraftSignature(baseDraft));
        const draft = loadFormDraft<ProdutoFormDraft>(draftKey) ?? baseDraft;
        setNome(draft.nome);
        setNcm(draft.ncm ?? "");
        setPrecoVenda(draft.precoVenda);
        setCategoriaSelect(draft.categoriaSelect);
        setCategoriaOutra(draft.categoriaOutra);
        setQuantidadeMinima(draft.quantidadeMinima);
        setDescricao(draft.descricao);
        setExibirNoCardapio(draft.exibirNoCardapio);
        setControlaEstoque(draft.controlaEstoque);
        setUnidadeVenda(draft.unidadeVenda);
        setEstoqueInicial(draft.estoqueInicial);
        setImagemUrl(draft.imagemUrl);
        setImagemPreview(resolveImagemPreview(draft.imagemUrl));
        setImagemFile(null);
        setImagemMarkedForRemoval(draft.imagemMarkedForRemoval);
        setCustoManual(draft.custoManual);
        setComposicaoItems(draft.composicaoItems);
        draftHydratedRef.current = true;
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        toast.error("Erro ao carregar produto");
        onCancelRef.current();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, produtoId, draftKey]);

  const currentDraft = useMemo(
    () => buildProdutoFormDraft({
      nome,
      ncm,
      precoVenda,
      categoriaSelect,
      categoriaOutra,
      quantidadeMinima,
      descricao,
      exibirNoCardapio,
      controlaEstoque,
      unidadeVenda,
      estoqueInicial,
      imagemUrl,
      imagemMarkedForRemoval,
      custoManual,
      composicaoItems,
    }),
    [
      nome,
      ncm,
      precoVenda,
      categoriaSelect,
      categoriaOutra,
      quantidadeMinima,
      descricao,
      exibirNoCardapio,
      controlaEstoque,
      unidadeVenda,
      estoqueInicial,
      imagemUrl,
      imagemMarkedForRemoval,
      custoManual,
      composicaoItems,
    ],
  );

  const produtoFormDirty = useMemo(
    () => originalDraftSignature != null && (produtoFormDraftSignature(currentDraft) !== originalDraftSignature || imagemFile != null),
    [currentDraft, originalDraftSignature, imagemFile],
  );

  useEffect(() => {
    if (loading || !draftHydratedRef.current) return;
    if (draftConcluidoRef.current) return;
    if (!produtoFormDirty) {
      clearFormDraft(draftKey, { suppressAutosave: false });
      return;
    }
    saveFormDraft(draftKey, currentDraft);
  }, [currentDraft, draftKey, loading, produtoFormDirty]);

  const precoVendaNum = parseNumeroBR(precoVenda) ?? 0;
  const custoManualNum = parseNumeroBR(custoManual) ?? 0;

  const custoTotal = useMemo(() => {
    if (temComposicao) {
      return calcularCustoTotalComposicao(composicaoItems, insumosPorId);
    }
    return custoManualNum > 0 ? custoManualNum : 0;
  }, [temComposicao, composicaoItems, insumosPorId, custoManualNum]);

  const cmvAtual = calcularCmvAtual(custoTotal, precoVendaNum);
  const precoIdeal = calcularPrecoIdeal(custoTotal, cmvIdeal);
  const cmvIdealLabel = cmvIdeal > 0 ? `${cmvIdeal % 1 === 0 ? cmvIdeal.toFixed(0) : cmvIdeal.toFixed(2).replace(".", ",")}%` : "0%";
  const lucroPorItem = calcularLucroPorItem(precoVendaNum, custoTotal);
  const margemLucro = calcularMargemLucroPercent(lucroPorItem, precoVendaNum);
  const margemLabel = `${margemLucro % 1 === 0 ? margemLucro.toFixed(0) : margemLucro.toFixed(2).replace(".", ",")}% de margem`;

  const produtoFormShowAction = useMemo(() => {
    const categoriaFinal =
      categoriaSelect === OPCAO_CATEGORIA_OUTRA ? categoriaOutra.trim() : categoriaSelect.trim();
    const precoNum = parseNumeroBR(precoVenda);
    return nome.trim() !== "" && precoNum !== null && precoNum >= 0 && categoriaFinal !== "" && produtoFormDirty;
  }, [nome, precoVenda, categoriaSelect, categoriaOutra, produtoFormDirty]);

  const composicaoModalShowAction = useMemo(() => {
    const hasValid = modalDraft.some((i) => (parseFloat(i.quantidade.replace(",", ".")) || 0) > 0);
    const changed = JSON.stringify(modalDraft) !== composicaoModalBaseline;
    return hasValid && changed;
  }, [modalDraft, composicaoModalBaseline]);

  const abrirModalComposicao = () => {
    const draft = composicaoItems.map((c) => ({ ...c }));
    setModalDraft(draft);
    setComposicaoModalBaseline(JSON.stringify(draft));
    setInsumoBusca("");
    setModalComposicaoOpen(true);
  };

  const adicionarInsumoPorId = (id: number) => {
    if (id <= 0) return;
    const insumo = insumos.find((i) => i.id === id);
    if (!insumo || modalDraft.some((c) => c.insumo_id === id)) return;
    const base = (insumo.unidade ?? "un").trim() || "un";
    setModalDraft((prev) => [
      ...prev,
      {
        insumo_id: id,
        insumo_nome: insumo.nome,
        unidade: unidadeCanonicaParaBase(base),
        unidade_base: base,
        quantidade: "",
      },
    ]);
    setInsumoBusca("");
    requestAnimationFrame(() => {
      document.getElementById("composicao_insumo_busca")?.focus();
    });
  };

  const salvarModalComposicao = () => {
    const validos = modalDraft.filter((i) => {
      const q = parseFloat(i.quantidade.replace(",", ".")) || 0;
      return q > 0;
    });
    setComposicaoItems(validos);
    setModalComposicaoOpen(false);
  };

  const removerComposicao = (insumoId: number) => {
    setComposicaoItems((prev) => prev.filter((c) => c.insumo_id !== insumoId));
  };

  const handleExcluirCategoria = async (category: { id: number; nome: string }) => {
    const snapshotCats = categoriasList;
    const nomeCat = category.nome.trim();

    setCategoriasList((prev) => prev.filter((c) => c.nome.trim().toLowerCase() !== nomeCat.toLowerCase()));
    if (categoriaSelect === category.nome) {
      setCategoriaSelect("");
      setCategoriaOutra("");
    }

    try {
      const res = await api.excluirCategoria(category.id, category.nome);
      setCategoriasList(res.categorias);
    } catch (e) {
      setCategoriasList(snapshotCats);
      console.error(e);
      toast.error(e instanceof Error ? e.message : "Erro ao remover categoria");
    }
  };

  const limparInputImagem = () => {
    if (imagemInputRef.current) imagemInputRef.current.value = "";
  };

  const revokePreviewBlob = () => {
    if (imagemPreviewBlobRef.current) {
      URL.revokeObjectURL(imagemPreviewBlobRef.current);
      imagemPreviewBlobRef.current = null;
    }
  };

  const resetNovoProdutoForm = () => {
    const empty = produtoFormEmptyDraft();
    revokePreviewBlob();
    setNome(empty.nome);
    setNcm(empty.ncm);
    setPrecoVenda(empty.precoVenda);
    setCategoriaSelect(empty.categoriaSelect);
    setCategoriaOutra(empty.categoriaOutra);
    setQuantidadeMinima(empty.quantidadeMinima);
    setDescricao(empty.descricao);
    setExibirNoCardapio(empty.exibirNoCardapio);
    setControlaEstoque(empty.controlaEstoque);
    setUnidadeVenda(empty.unidadeVenda);
    setEstoqueInicial(empty.estoqueInicial);
    setImagemUrl(empty.imagemUrl);
    setImagemPreview(null);
    setImagemFile(null);
    setImagemMarkedForRemoval(empty.imagemMarkedForRemoval);
    setCustoManual(empty.custoManual);
    setComposicaoItems(empty.composicaoItems);
    setModalComposicaoOpen(false);
    setModalDraft([]);
    setComposicaoModalBaseline("[]");
    setInsumoBusca("");
    limparInputImagem();
    setOriginalDraftSignature(produtoFormDraftSignature(empty));
  };

  const handleImagemSelect = (file: File) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formato não suportado. Use JPG, JPEG, PNG ou WEBP.");
      limparInputImagem();
      return;
    }
    if (file.size > MAX_IMAGEM_BYTES) {
      toast.error("Imagem muito grande (máx. 5 MB)");
      limparInputImagem();
      return;
    }
    revokePreviewBlob();
    const blobUrl = URL.createObjectURL(file);
    imagemPreviewBlobRef.current = blobUrl;
    setImagemFile(file);
    setImagemPreview(blobUrl);
    setImagemMarkedForRemoval(false);
    limparInputImagem();
  };

  const handleRemoverImagem = () => {
    if (uploadingImagem) return;
    revokePreviewBlob();
    setImagemPreview(null);
    setImagemFile(null);
    setImagemMarkedForRemoval(true);
    limparInputImagem();
  };

  const aplicarPrecoSugerido = () => {
    if (precoIdeal == null) return;
    setPrecoVenda(numberToMoneyMask(precoIdeal));
  };

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (!nome.trim()) {
      toast.error("Nome do produto é obrigatório");
      return;
    }
    const precoNum = parseNumeroBR(precoVenda);
    if (precoNum === null) {
      toast.error("Preço de venda é obrigatório");
      return;
    }
    if (precoNum < 0) {
      toast.error("Preço de venda inválido");
      return;
    }
    const categoriaFinal =
      categoriaSelect === OPCAO_CATEGORIA_OUTRA ? categoriaOutra.trim() : categoriaSelect.trim();
    if (!categoriaFinal) {
      toast.error("Selecione ou informe a categoria.");
      return;
    }
    if (categoriaSelect === OPCAO_CATEGORIA_OUTRA && !isAdmin) {
      toast.error("Apenas administradores podem cadastrar uma categoria nova.");
      return;
    }
    const ncmPayload = ncm.trim();
    if (ncmPayload !== "" && !/^\d{8}$/.test(ncmPayload)) {
      toast.error("NCM deve conter exatamente 8 digitos.");
      return;
    }

    const quantidadeMinimaNum = temComposicao || !quantidadeMinima.trim() ? 0 : parseQuantidadeInput(quantidadeMinima);
    if (quantidadeMinimaNum === null) {
      toast.error("Quantidade mínima deve ser um número maior ou igual a zero.");
      return;
    }
    const estoqueInicialNum = !isEdit && !temComposicao && estoqueInicial.trim() ? parseQuantidadeInput(estoqueInicial) : null;
    if (!isEdit && !temComposicao && estoqueInicial.trim() && estoqueInicialNum === null) {
      toast.error("Estoque inicial deve ser um número maior ou igual a zero.");
      return;
    }

    const composicaoPayload = temComposicao
      ? composicaoItems
          .filter((i) => {
            const q = parseFloat(i.quantidade.replace(",", ".")) || 0;
            return q > 0;
          })
          .map((i) => {
            const mult = FRACAO_MULT[i.unidade?.trim() ?? "un"] ?? 1;
            const qty = (parseFloat(i.quantidade.replace(",", ".")) || 0) * mult;
            const unidadeEnviar = mult !== 1 ? "un" : (i.unidade?.trim() || "un");
            return { insumo_id: i.insumo_id, quantidade: qty, unidade: unidadeEnviar };
          })
      : undefined;

    const valorAtual = !temComposicao ? parseNumeroBR(custoManual) : undefined;
    const descricaoTrim = descricao.trim() || null;
    const flagsPayload = {
      exibir_no_cardapio: exibirNoCardapio,
      controla_estoque: controlaEstoque,
      unidade_venda: unidadeVenda,
    };
    /** Mantém URL antiga no update se houver novo arquivo (upload troca depois); null se marcada para remoção. */
    const imagemUrlParaSalvar =
      imagemFile != null ? imagemUrl : imagemMarkedForRemoval ? null : imagemUrl;

    try {
      setSaving(true);
      setUploadingImagem(imagemFile != null);
      let saved: Produto;
      if (isEdit) {
        saved = await api.atualizarProduto(produtoId!, {
          nome: nome.trim(),
          categoria: categoriaFinal,
          possui_composicao: temComposicao,
          preco_venda: precoNum,
          quantidade_minima: quantidadeMinimaNum,
          composicao: composicaoPayload,
          valor_atual: valorAtual,
          descricao: descricaoTrim,
          imagem_url: imagemUrlParaSalvar,
          ncm: ncmPayload || null,
          ...flagsPayload,
        }, imagemFile);
      } else {
        saved = await api.criarProduto({
          nome: nome.trim(),
          categoria: categoriaFinal,
          possui_composicao: temComposicao,
          preco_venda: precoNum,
          quantidade_minima: quantidadeMinimaNum,
          composicao: composicaoPayload,
          valor_atual: valorAtual,
          descricao: descricaoTrim,
          imagem_url: null,
          ncm: ncmPayload || null,
          ...flagsPayload,
          ...(estoqueInicialNum != null ? { estoque_inicial: estoqueInicialNum } : {}),
        }, imagemFile);
      }

      toast.success(isEdit ? "Produto atualizado." : "Produto criado.");
      draftConcluidoRef.current = true;
      clearFormDraft(draftKey);
      if (isEdit) {
        revokePreviewBlob();
        setImagemUrl(saved.imagem_url ?? null);
        setImagemPreview(resolveImagemPreview(saved.imagem_url ?? null));
        setImagemFile(null);
        setImagemMarkedForRemoval(false);
        limparInputImagem();
        setOriginalDraftSignature(produtoFormDraftSignature({
          ...currentDraft, imagemUrl: saved.imagem_url ?? null, imagemMarkedForRemoval: false,
        }));
      } else {
        resetNovoProdutoForm();
      }
      onSaved();
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Erro ao salvar produto");
    } finally {
      setSaving(false);
      setUploadingImagem(false);
    }
  };

  if (loading) {
    return <PageContentLoader />;
  }

  const metricCards = [
    { label: "Custo total", sub: "Por unidade", value: formatBRL(custoTotal) },
    { label: "CMV ideal", sub: "Meta recomendada", value: cmvIdealLabel },
    {
      label: "CMV atual",
      sub: "Sobre o preço",
      value: `${cmvAtual % 1 === 0 ? cmvAtual.toFixed(0) : cmvAtual.toFixed(2).replace(".", ",")}%`,
      valueClass: corCmvAtual(cmvAtual, cmvIdeal),
    },
    { label: "Preço sugerido", sub: "Por CMV ideal", value: formatBRLMetrica(precoIdeal) },
    {
      label: "Lucro por item",
      sub: margemLabel,
      value: formatBRL(lucroPorItem),
    },
  ];

  const handleCancel = () => {
    draftConcluidoRef.current = true;
    clearFormDraft(draftKey);
    onCancel();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className={PAGE_TITLE_CLASS}>
          {isEdit ? "Editar Produto" : "Adicionar Produto"}
        </h1>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button type="button" className={BTN_OUTLINE_ACTION} onClick={handleCancel}>
            <ArrowLeft className="h-4 w-4 shrink-0" />
            Voltar
          </Button>
          {produtoFormShowAction ? (
            <Button
              type="button"
              className={BTN_PAGE_PRIMARY_ACTION}
              style={primaryButtonStyle}
              disabled={saving || uploadingImagem}
              onClick={() => void handleSalvar({ preventDefault: () => {} } as React.FormEvent)}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <Check className="h-4 w-4 shrink-0" />
              )}
              Salvar
            </Button>
          ) : null}
        </div>
      </div>

      <form onSubmit={handleSalvar} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-stretch">
          {/* 1 — Informações principais */}
          <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <SectionBadge n={1} />
              <h2 className="text-base font-semibold text-foreground">Informações principais</h2>
            </div>
            <div className="space-y-3 sm:space-y-0">
              <div
                className={
                  exibirNoCardapio
                    ? "grid grid-cols-1 gap-3 sm:grid-cols-[minmax(160px,200px)_1fr] sm:items-start"
                    : "space-y-3"
                }
              >
                {exibirNoCardapio ? (
                  <div className="flex w-full flex-col gap-2">
                    <div className="relative w-full">
                      <button
                        type="button"
                        className="flex h-[180px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50/80 p-3 text-center hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-70"
                        onClick={() => !uploadingImagem && imagemInputRef.current?.click()}
                        disabled={uploadingImagem}
                      >
                        {uploadingImagem ? (
                          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
                        ) : imagemPreview ? (
                          <img
                            src={imagemPreview}
                            alt=""
                            className="max-h-[148px] w-full rounded-md object-contain"
                          />
                        ) : (
                          <>
                            <ImageIcon className="h-8 w-8 text-muted-foreground/70" aria-hidden />
                            <span className="text-sm font-medium text-foreground">Adicionar imagem do produto</span>
                            <span className="text-xs text-muted-foreground">PNG JPG WEBP · Máx 5MB</span>
                          </>
                        )}
                      </button>
                      {imagemPreview && !uploadingImagem ? (
                        <button
                          type="button"
                          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-gray-600 shadow-sm ring-1 ring-gray-200 hover:bg-white hover:text-red-600"
                          onClick={() => void handleRemoverImagem()}
                          aria-label="Remover imagem"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                    <input
                      ref={imagemInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        handleImagemSelect(file);
                      }}
                    />
                  </div>
                ) : null}

                <div className="w-full space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="produto_nome">
                      Nome <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="produto_nome"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      required
                      className="border-gray-200 bg-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="produto_categoria">
                      Categoria <span className="text-red-500">*</span>
                    </Label>
                    <CategoryFormSelect
                      id="produto_categoria"
                      value={categoriaSelect}
                      onChange={setCategoriaSelect}
                      categories={categoriasList}
                      onDeleteCategory={handleExcluirCategoria}
                      showOutrasOption
                      outrasValue={OPCAO_CATEGORIA_OUTRA}
                      outrasLabel="Outras"
                      className="w-full"
                    />
                  </div>
                  {categoriaSelect === OPCAO_CATEGORIA_OUTRA && (
                    <div className="space-y-2">
                      <Label htmlFor="produto_categoria_outra">Nova categoria</Label>
                      <Input
                        id="produto_categoria_outra"
                        value={categoriaOutra}
                        onChange={(e) => setCategoriaOutra(e.target.value)}
                        className="border-gray-200 bg-white"
                      />
                    </div>
                  )}
                  <FormToggle
                    id="produto_exibir_cardapio"
                    checked={exibirNoCardapio}
                    onCheckedChange={setExibirNoCardapio}
                    label="Exibir no cardápio digital"
                    helper="Quando ativo, o produto aparece no cardápio digital."
                  />
                </div>
              </div>

              {exibirNoCardapio ? (
                <div className="w-full space-y-2">
                  <Label htmlFor="produto_descricao">Descrição</Label>
                  <textarea
                    id="produto_descricao"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value.slice(0, MAX_DESCRICAO))}
                    rows={4}
                    maxLength={MAX_DESCRICAO}
                    className="flex min-h-[96px] w-full resize-none rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Descrição exibida no cardápio digital"
                  />
                  <p className="text-right text-xs text-muted-foreground">
                    {descricao.length}/{MAX_DESCRICAO}
                  </p>
                </div>
              ) : null}
            </div>
          </div>

          {/* 2 — Preço e estoque */}
          <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <SectionBadge n={2} />
              <h2 className="text-base font-semibold text-foreground">Preço e estoque</h2>
            </div>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="produto_preco">
                    Preço de venda <span className="text-red-500">*</span>
                  </Label>
                  <MoneyInput
                    id="produto_preco"
                    value={precoVenda}
                    onChange={setPrecoVenda}
                    placeholder="0,00"
                    required
                    className="border-gray-200 bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="produto_unidade_venda">Unidade de venda</Label>
                  <Select
                    id="produto_unidade_venda"
                    value={unidadeVenda}
                    onChange={(e) => setUnidadeVenda(normalizarUnidadeVenda(e.target.value))}
                    className="border-gray-200 bg-white"
                  >
                    {UNIDADES_VENDA.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </Select>
                </div>
                {nfceDisponivel ? (
                  <div className="space-y-2">
                    <Label htmlFor="produto_ncm">NCM</Label>
                    <Input
                      id="produto_ncm"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={8}
                      value={ncm}
                      onChange={(e) => setNcm(e.target.value.replace(/\D/g, "").slice(0, 8))}
                      placeholder="00000000"
                      className="border-gray-200 bg-white"
                    />
                  </div>
                ) : null}
              </div>
              <div>
                <FormToggle
                  id="produto_controla_estoque"
                  checked={controlaEstoque}
                  onCheckedChange={setControlaEstoque}
                  label="Controla estoque"
                  helper="Quando ativo, o sistema desconta o estoque nas vendas."
                />
              </div>
              {!temComposicao ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {controlaEstoque ? (
                    <div className="space-y-2">
                      <Label htmlFor="produto_qtd_min">Quantidade mínima</Label>
                      <Input
                        id="produto_qtd_min"
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        value={quantidadeMinima}
                        onChange={(e) => setQuantidadeMinima(e.target.value)}
                        onBlur={() => {
                          const value = parseQuantidadeInput(quantidadeMinima);
                          if (value !== null) setQuantidadeMinima(formatQuantidadeInput(value));
                        }}
                        className="border-gray-200 bg-white"
                      />
                    </div>
                  ) : null}
                  <div className={`space-y-2${controlaEstoque ? "" : " sm:col-span-2"}`}>
                    <Label htmlFor="produto_custo">Custo unitário</Label>
                    <MoneyInput
                      id="produto_custo"
                      value={custoManual}
                      onChange={setCustoManual}
                      placeholder="0,00"
                      className="border-gray-200 bg-white"
                    />
                  </div>
                </div>
              ) : null}
              {!isEdit && !temComposicao && controlaEstoque ? (
                <div className="space-y-2">
                  <Label htmlFor="produto_estoque_inicial">Estoque inicial</Label>
                  <Input
                    id="produto_estoque_inicial"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    value={estoqueInicial}
                    onChange={(e) => setEstoqueInicial(e.target.value)}
                    onBlur={() => {
                      const value = parseQuantidadeInput(estoqueInicial);
                      if (value !== null) setEstoqueInicial(formatQuantidadeInput(value));
                    }}
                    placeholder="0"
                    className="border-gray-200 bg-white"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Métricas */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          {metricCards.map((card, index) => (
            <div
              key={card.label}
              className={`rounded-xl border border-gray-200 bg-white px-4 py-4 text-center shadow-sm${
                index === metricCards.length - 1 ? " col-span-2 lg:col-span-1" : ""
              }`}
            >
              <p className="text-xs font-medium text-muted-foreground">{card.label}</p>
              <p
                className={`mt-1 break-words text-xl font-semibold tabular-nums ${card.valueClass ?? "text-foreground"}`}
              >
                {card.value}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{card.sub}</p>
            </div>
          ))}
        </div>

        {/* 3 — Composição + sugestão */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_280px] lg:items-start">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <SectionBadge n={3} />
                <h2 className="text-base font-semibold text-foreground">Ingredientes e insumos</h2>
              </div>
              <button
                type="button"
                onClick={abrirModalComposicao}
                className="inline-flex h-10 items-center gap-2 rounded-md px-3 text-sm font-medium text-white hover:opacity-90"
                style={primaryButtonStyle}
              >
                <Plus className="h-4 w-4 shrink-0" />
                Adicionar composição
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="gs-management-table gs-management-table-specialized w-full min-w-[520px] text-sm">
                <thead className={TABLE_HEAD_CLASS}>
                  <tr className="border-b border-gray-200">
                    <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Insumos</th>
                    <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Quantidade</th>
                    <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Unidade</th>
                    <th className={`px-4 py-3 text-left ${TABLE_TH_CLASS}`}>Custo (R$)</th>
                    <th className={`w-24 px-4 py-3 text-right ${TABLE_TH_CLASS}`}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {composicaoItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhum insumo na composição.
                      </td>
                    </tr>
                  ) : (
                    composicaoItems.map((item) => {
                      const ins = insumosPorId.get(item.insumo_id);
                      const custo = custoLinhaComposicao(
                        ins?.valorAtual,
                        item.unidade_base,
                        item.quantidade,
                        item.unidade,
                      );
                      return (
                        <tr key={item.insumo_id} className="border-b border-gray-100 last:border-0">
                          <td className="px-4 py-3 font-medium text-foreground">{formatDisplayText(item.insumo_nome)}</td>
                          <td className="px-4 py-3 tabular-nums text-foreground">{item.quantidade || "—"}</td>
                          <td className="px-4 py-3 text-foreground">{item.unidade || "—"}</td>
                          <td className="px-4 py-3 tabular-nums text-foreground">
                            {custo != null ? formatBRL(custo) : "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label="Editar composição"
                                onClick={abrirModalComposicao}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                aria-label="Remover"
                                onClick={() => removerComposicao(item.insumo_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 text-center shadow-sm">
            <div className="mb-3 flex items-center justify-center gap-2">
              <Lightbulb className="h-5 w-5 text-client-primary" aria-hidden />
              <h2 className="text-base font-semibold text-foreground">Sugestão da GrandSystem</h2>
            </div>
            <p className="text-xs text-muted-foreground">
              Com base no CMV ideal ({cmvIdealLabel}), o preço sugerido para este produto é:
            </p>
            <p className="mt-4 text-3xl font-bold tabular-nums text-client-primary">
              {formatBRLMetrica(precoIdeal)}
            </p>
            {precoIdeal != null ? (
              <Button
                type="button"
                className={`mt-4 w-full ${BTN_PAGE_PRIMARY_ACTION}`}
                style={primaryButtonStyle}
                onClick={aplicarPrecoSugerido}
              >
                <Wand2 className="h-4 w-4 shrink-0" />
                Aplicar preço sugerido
              </Button>
            ) : null}
          </div>
        </div>
      </form>

      {modalComposicaoOpen && (
        <StandardModal
          open={modalComposicaoOpen}
          title="Adicionar Composição"
          titleId="modal-composicao-title"
          onClose={() => setModalComposicaoOpen(false)}
          panelClassName="max-w-xl !overflow-y-visible overflow-x-hidden"
        >
          <div className={`min-w-0 overflow-visible ${MODAL_BODY_CLASS}`}>
            <p className="text-sm text-muted-foreground">
              Selecione insumos já cadastrados e informe a quantidade de cada um.
            </p>
            <div className="w-full min-w-0">
              <Autocomplete
                id="composicao_insumo_busca"
                options={insumoAutocompleteOptions}
                value={insumoBusca}
                onValueChange={setInsumoBusca}
                onSelect={(opt) => adicionarInsumoPorId(Number(opt.value))}
                placeholder="Digite o nome do insumo..."
                requireQueryForSuggestions
                listLayout="inline"
                maxVisibleItems={5}
                className="h-10 border-gray-200 bg-white"
              />
            </div>

            {modalDraft.length > 0 && (
              <ul className="space-y-2">
                {modalDraft.map((item) => {
                  const opcoes = opcoesUnidadePorBase(item.unidade_base || "un");
                  return (
                    <li
                      key={item.insumo_id}
                      className="grid items-center gap-2 rounded border border-gray-100 bg-muted/20 p-3 sm:grid-cols-[1fr_5rem_5rem_2.5rem]"
                    >
                      <span className="text-sm font-medium">{formatDisplayText(item.insumo_nome)}</span>
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        placeholder="Qtd"
                        value={item.quantidade}
                        onChange={(e) =>
                          setModalDraft((prev) =>
                            prev.map((c) =>
                              c.insumo_id === item.insumo_id ? { ...c, quantidade: e.target.value } : c,
                            ),
                          )
                        }
                        className="h-9 border-gray-200"
                      />
                      <Select
                        value={item.unidade}
                        onChange={(e) =>
                          setModalDraft((prev) =>
                            prev.map((c) =>
                              c.insumo_id === item.insumo_id ? { ...c, unidade: e.target.value } : c,
                            ),
                          )
                        }
                        className="h-9 px-2 text-sm"
                      >
                        {opcoes.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-destructive hover:text-destructive"
                        onClick={() => setModalDraft((prev) => prev.filter((c) => c.insumo_id !== item.insumo_id))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}

            <ModalPrimaryAction
              show={composicaoModalShowAction}
              type="button"
              onClick={salvarModalComposicao}
            >
              Salvar Composições
            </ModalPrimaryAction>
          </div>
        </StandardModal>
      )}
    </div>
  );
}
